import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { statsService } from "../services";
import JobOffersModal from "../components/JobOffersModal";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ReferenceLine,
  Cell,
  PieChart,
  Pie,
  RadialBarChart,
  RadialBar,
} from "recharts";
import {
  FiTrendingUp,
  FiAward,
  FiClock,
  FiPlus,
  FiBarChart2,
  FiList,
  FiTarget,
  FiActivity,
  FiZap,
  FiCheckCircle,
  FiInfo,
} from "react-icons/fi";
import { format, parseISO } from "date-fns";

// --- Custom Components ---

const HeroSection = ({ t, navigate, stats }) => (
  <motion.div
    className="dashboard-hero"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1 }}
  >
    <div className="dashboard-hero-overlay"></div>
    <div className="dashboard-hero-content">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <h1 className="dashboard-hero-title">{t("dashboard.title")}</h1>
        <p className="dashboard-hero-subtitle">
          {t("dashboard.subtitle") || "Alcanza tu máximo potencial profesional"}
        </p>
      </motion.div>

      <motion.div
        className="dashboard-hero-stats"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <div className="hero-stat-item">
          <FiTarget className="hero-stat-icon" />
          <div className="hero-stat-content">
            <span className="hero-stat-value">
              {stats?.totalInterviews || 0}
            </span>
            <span className="hero-stat-label">
              {t("dashboard.totalInterviews")}
            </span>
          </div>
        </div>
        <div className="hero-stat-divider"></div>
        <div className="hero-stat-item">
          <FiCheckCircle className="hero-stat-icon" />
          <div className="hero-stat-content">
            <span className="hero-stat-value">
              {stats?.completedInterviews || 0}
            </span>
            <span className="hero-stat-label">
              {t("dashboard.completedInterviews")}
            </span>
          </div>
        </div>
        <div className="hero-stat-divider"></div>
        <div className="hero-stat-item">
          <FiZap className="hero-stat-icon" />
          <div className="hero-stat-content">
            <span className="hero-stat-value">
              {(stats?.averageScore || 0).toFixed(1)}%
            </span>
            <span className="hero-stat-label">
              {t("dashboard.averageScore")}
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="dashboard-hero-actions"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <button
          onClick={() => navigate("/interviews/create")}
          className="hero-btn-primary"
        >
          <FiPlus /> {t("interview.newInterview")}
        </button>
        <button
          onClick={() => navigate("/interviews")}
          className="hero-btn-secondary"
        >
          <FiList /> {t("interview.myInterviews")}
        </button>
      </motion.div>
    </div>
  </motion.div>
);

const MinimalStatCard = ({ icon, title, value, subtitle, color, delay }) => (
  <motion.div
    className={`minimal-stat-card minimal-stat-${color}`}
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    whileHover={{ y: -5, scale: 1.02 }}
    transition={{ duration: 0.4, delay }}
  >
    <div className="minimal-stat-icon-wrapper">
      <div className={`minimal-stat-icon minimal-stat-icon-${color}`}>
        {icon}
      </div>
    </div>
    <div className="minimal-stat-content">
      <p className="minimal-stat-label">{title}</p>
      <h3 className="minimal-stat-value">{value}</h3>
      {subtitle && <p className="minimal-stat-subtitle">{subtitle}</p>}
    </div>
  </motion.div>
);

const CustomTooltip = ({ active, payload, label, t }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isDark = document.documentElement.classList.contains("dark");

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 25,
          duration: 0.3,
        }}
        className={`minimal-tooltip ${isDark ? "" : "light"}`}
      >
        <p className="minimal-tooltip-date">
          {data.fullDate || data.date || "N/A"}
        </p>
        <div className="minimal-tooltip-content">
          <div className="minimal-tooltip-row">
            <span className="minimal-tooltip-label">
              {t("interview.score")}
            </span>
            <span className="minimal-tooltip-value score">
              {typeof data.score === "number" ? data.score.toFixed(1) : "0.0"}%
            </span>
          </div>
          {payload[1] && (
            <div className="minimal-tooltip-row">
              <span className="minimal-tooltip-label">
                {t("dashboard.interviews")}
              </span>
              <span className="minimal-tooltip-value count">
                {data.count || 0}
              </span>
            </div>
          )}
        </div>
      </motion.div>
    );
  }
  return null;
};

const CustomPieTooltip = ({ active, payload, t }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const total = payload.reduce(
      (sum, item) => sum + (item.payload?.value || 0),
      0
    );
    const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    const isDark = document.documentElement.classList.contains("dark");

    return (
      <div className="minimal-tooltip pie">
        <div className="minimal-tooltip-header">
          <div
            className="minimal-tooltip-color"
            style={{ backgroundColor: payload[0]?.fill || "#666" }}
          ></div>
          <p className="minimal-tooltip-name">{data.name}</p>
        </div>
        <div className="minimal-tooltip-content">
          <div className="minimal-tooltip-row">
            <span className="minimal-tooltip-label">
              {t("dashboard.interviews")}
            </span>
            <span className="minimal-tooltip-value">{data.value || 0}</span>
          </div>
          <div className="minimal-tooltip-row">
            <span className="minimal-tooltip-label">Porcentaje</span>
            <span className="minimal-tooltip-value">{percentage}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const StatCard = ({
  title,
  value,
  icon,
  colorClass,
  delay,
  isLarge,
  isCircular,
}) => (
  <motion.div
    className={`stat-card stat-card-${colorClass} ${
      isLarge ? "stat-card-large" : ""
    } ${isCircular ? "stat-card-circular" : ""}`}
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    whileHover={{ y: -5 }}
  >
    <div className="stat-card-top">
      <div className={`stat-card-icon ${colorClass}`}>{icon}</div>
      <p className="stat-card-label">{title}</p>
    </div>

    <p className="stat-card-value">{value}</p>
  </motion.div>
);

const MinimalChartContainer = ({ title, subtitle, children, delay = 0, infoTooltip = null }) => (
  <motion.div
    className="minimal-chart-container"
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
  >
    <div className="minimal-chart-header">
      <div>
        <div className="minimal-chart-title-wrapper">
          <h2 className="minimal-chart-title">{title}</h2>
          {infoTooltip && (
            <div className="minimal-chart-info-tooltip">
              <FiInfo className="minimal-chart-info-icon" />
              <span className="minimal-chart-info-text">{infoTooltip}</span>
            </div>
          )}
        </div>
        {subtitle && <p className="minimal-chart-subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="minimal-chart-content">{children}</div>
  </motion.div>
);

// --- Main Dashboard Component ---

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalInterviews: 0,
    completedInterviews: 0,
    averageScore: 0,
    totalDuration: 0,
    interviewsByProfession: {},
  });
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfession, setSelectedProfession] = useState(null);
  const [jobOffersModalOpen, setJobOffersModalOpen] = useState(false);

  const cardAnimationDelay = 0.2;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsResponse, trendsResponse] = await Promise.all([
        statsService.getUserStats(),
        statsService.getPerformanceTrends(),
      ]);

      if (statsResponse?.data?.stats) {
        setStats(statsResponse.data.stats);
      } else {
        setStats({
          totalInterviews: 0,
          completedInterviews: 0,
          averageScore: 0,
          totalDuration: 0,
          interviewsByProfession: {},
        });
      }

      if (
        trendsResponse?.data?.trends &&
        Array.isArray(trendsResponse.data.trends)
      ) {
        const formattedTrends = trendsResponse.data.trends
          .filter((t) => t.date) // Filtrar tendencias sin fecha
          .map((t) => {
            try {
              const parsedDate = parseISO(t.date);
              return {
                ...t,
                date: format(parsedDate, "MMM d"),
                fullDate: format(parsedDate, "MMM d, yyyy"),
              };
            } catch (error) {
              console.error("Error parsing date:", t.date, error);
              return null;
            }
          })
          .filter((t) => t !== null); // Filtrar fechas que no pudieron parsearse
        setTrends(formattedTrends);
      } else {
        setTrends([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);

      // Si el error es de autenticación, el interceptor ya redirigirá
      if (error.response?.status === 401) {
        toast.error(
          t("errors.sessionExpired") ||
            "Sesión expirada. Por favor, inicia sesión nuevamente."
        );
        // El interceptor ya manejará la redirección
        return;
      }

      toast.error(t("errors.fetchDataError") || "Error al cargar los datos");
      setStats({
        totalInterviews: 0,
        completedInterviews: 0,
        averageScore: 0,
        totalDuration: 0,
        interviewsByProfession: {},
      });
      setTrends([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const COLORS = [
    "#C4B7CB",
    "#98E2C6",
    "#D4BDDE",
    "#B3EBD6",
    "#A89FB5",
    "#7DD4B5",
  ];

  const professionData = Object.entries(
    stats?.interviewsByProfession || {}
  ).map(([profession, count]) => ({
    name: profession,
    value: count,
  }));

  const averageScore = stats?.averageScore || 0;

  // Detectar modo oscuro
  const isDark = document.documentElement.classList.contains("dark");

  // Colores para los gráficos según el modo
  const chartColors = isDark
    ? {
        background: "#1E1E1E",
        grid: "#333333",
        axis: "#9CA3AF",
        text: "#E0E0E0",
        brushFill: "#2C2C2C",
        brushSelectedFill: "rgba(152, 226, 198, 0.2)",
        brushStroke: "#98E2C6",
        brushSelectedStroke: "#98E2C6",
        brushText: "#B0B0B0",
      }
    : {
        background: "#FFFFFF",
        grid: "#E5E7EB",
        axis: "#9CA3AF",
        text: "#374151",
        brushFill: "rgba(249, 250, 251, 0.5)",
        brushSelectedFill: "rgba(152, 226, 198, 0.15)",
        brushStroke: "#98E2C6",
        brushSelectedStroke: "#7DD4B5",
        brushText: "#6B7280",
      };

  if (loading) {
    return (
      <div className="minimal-dashboard-loading">
        <motion.div
          className="minimal-loading-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <p className="minimal-loading-text">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className="minimal-dashboard">
      {/* Hero Section */}
      <HeroSection t={t} navigate={navigate} stats={stats} />

      {/* Main Content */}
      <div className="minimal-dashboard-content">
        {/* Performance Overview Section */}
        <motion.section
          className="minimal-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="minimal-section-header">
            <h2 className="minimal-section-title">
              {t("dashboard.performanceOverview") || "Resumen de rendimiento"}
            </h2>
            <p className="minimal-section-subtitle">
              {t("dashboard.performanceSubtitle") ||
                "Métricas clave de tus entrevistas"}
            </p>
          </div>

          <div className="minimal-stats-grid">
            <MinimalStatCard
              icon={<FiActivity />}
              title={t("dashboard.totalDuration")}
              value={`${Math.round((stats?.totalDuration || 0) / 60)}`}
              subtitle="minutos"
              color="purple"
              delay={0.1}
            />
            <MinimalStatCard
              icon={<FiBarChart2 />}
              title={t("dashboard.averageScore")}
              value={`${(stats?.averageScore || 0).toFixed(1)}`}
              subtitle="puntuación media"
              color="blue"
              delay={0.2}
            />
            <MinimalStatCard
              icon={<FiCheckCircle />}
              title="Tasa de Éxito"
              value={
                stats?.totalInterviews > 0
                  ? `${(
                      (stats.completedInterviews / stats.totalInterviews) *
                      100
                    ).toFixed(0)}%`
                  : "0%"
              }
              subtitle="completadas"
              color="green"
              delay={0.3}
            />
          </div>
        </motion.section>

        {/* Performance Trends Chart */}
        <MinimalChartContainer
          title={t("dashboard.performanceTrends")}
          subtitle={
            t("dashboard.trendsSubtitle") ||
            "Evolución de tu rendimiento a lo largo del tiempo"
          }
          delay={1}
        >
          {trends && trends.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart
                data={trends}
                margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
              >
                <defs>
                  <linearGradient
                    id="minimalGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop
                      offset="100%"
                      stopColor="#6366f1"
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.5} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDark ? "#2d2d2d" : "#e5e7eb"}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  stroke={isDark ? "#6b7280" : "#9ca3af"}
                  tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                  axisLine={{ stroke: isDark ? "#374151" : "#d1d5db" }}
                />
                <YAxis
                  yAxisId="left"
                  stroke={isDark ? "#6b7280" : "#9ca3af"}
                  tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                  axisLine={{ stroke: isDark ? "#374151" : "#d1d5db" }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={isDark ? "#6b7280" : "#9ca3af"}
                  tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                  axisLine={{ stroke: isDark ? "#374151" : "#d1d5db" }}
                />
                <Tooltip content={<CustomTooltip t={t} />} />
                <Area
                  type="monotone"
                  dataKey="score"
                  yAxisId="left"
                  stroke="#6366f1"
                  strokeWidth={3}
                  fill="url(#minimalGradient)"
                  dot={{
                    fill: "#6366f1",
                    strokeWidth: 2,
                    r: 5,
                    stroke: "#fff",
                  }}
                  activeDot={{ r: 7 }}
                />
                <Bar
                  dataKey="count"
                  yAxisId="right"
                  fill="url(#barGradient)"
                  radius={[6, 6, 0, 0]}
                  barSize={32}
                />
                <ReferenceLine
                  yAxisId="left"
                  y={averageScore}
                  stroke={isDark ? "#4b5563" : "#d1d5db"}
                  strokeDasharray="5 5"
                  strokeWidth={1.5}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="minimal-empty-state">
              <FiBarChart2 className="minimal-empty-icon" />
              <p className="minimal-empty-text">{t("dashboard.noTrends")}</p>
              <button
                onClick={() => navigate("/interviews/create")}
                className="minimal-empty-button"
              >
                {t("interview.newInterview")}
              </button>
            </div>
          )}
        </MinimalChartContainer>

        {/* Professions Distribution */}
        <MinimalChartContainer
          title={t("dashboard.interviewsByProfession")}
          subtitle={
            t("dashboard.professionsSubtitle") ||
            "Distribución de entrevistas por profesión"
          }
          delay={1.2}
          infoTooltip={t("jobOffers.clickSectorTooltip") || "Haz clic en un sector para ver ofertas de trabajo"}
        >
          {professionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <defs>
                  <filter id="minimalShadow">
                    <feDropShadow
                      dx="0"
                      dy="2"
                      stdDeviation="3"
                      floodColor={
                        isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"
                      }
                    />
                  </filter>
                </defs>
                <Pie
                  data={professionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={140}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ percent }) =>
                    percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""
                  }
                  onClick={(entry) => {
                    setSelectedProfession(entry.name);
                    setJobOffersModalOpen(true);
                  }}
                >
                  {professionData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke={isDark ? "#1a1a1a" : "#ffffff"}
                      strokeWidth={3}
                      filter="url(#minimalShadow)"
                      style={{ cursor: "pointer" }}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip t={t} />} />
                <Legend
                  verticalAlign="bottom"
                  height={60}
                  content={({ payload }) => (
                    <div className="minimal-pie-legend">
                      {payload.map((entry, index) => (
                        <div key={index} className="minimal-legend-item">
                          <div
                            className="minimal-legend-color"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="minimal-legend-label">
                            {entry.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="minimal-empty-state">
              <FiAward className="minimal-empty-icon" />
              <p className="minimal-empty-text">
                {t("dashboard.noProfessionData")}
              </p>
              <button
                onClick={() => navigate("/interviews/create")}
                className="minimal-empty-button"
              >
                {t("interview.newInterview")}
              </button>
            </div>
          )}
        </MinimalChartContainer>
      </div>

      {/* Job Offers Modal */}
      <JobOffersModal
        isOpen={jobOffersModalOpen}
        profession={selectedProfession}
        onClose={() => {
          setJobOffersModalOpen(false);
          setSelectedProfession(null);
        }}
      />
    </div>
  );
};

export default Dashboard;
